import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePageSEO } from "@/hooks/usePageSEO";

export default function FAQ() {
  const { t } = useLanguage();
  usePageSEO({
    title: "ISO27001KIT FAQ | Documents, AI Assistant & Pro Access",
    description: "Get answers about ISO27001KIT documents, AI Assistant limits, Pro access, payments, refunds, privacy, and support.",
    canonicalPath: "/faq",
  });

  const faqCategories = [
    {
      titleKey: "faq.category.general",
      questions: [
        { qKey: "faq.general.q1", aKey: "faq.general.a1" },
        { qKey: "faq.general.q2", aKey: "faq.general.a2" },
        { qKey: "faq.general.q3", aKey: "faq.general.a3" },
        { qKey: "faq.general.q4", aKey: "faq.general.a4" },
      ]
    },
    {
      titleKey: "faq.category.implementation",
      questions: [
        { qKey: "faq.implementation.q1", aKey: "faq.implementation.a1" },
        { qKey: "faq.implementation.q2", aKey: "faq.implementation.a2" },
        { qKey: "faq.implementation.q3", aKey: "faq.implementation.a3" },
        { qKey: "faq.implementation.q4", aKey: "faq.implementation.a4" },
      ]
    },
    {
      titleKey: "faq.category.certification",
      questions: [
        { qKey: "faq.certification.q1", aKey: "faq.certification.a1" },
        { qKey: "faq.certification.q2", aKey: "faq.certification.a2" },
        { qKey: "faq.certification.q3", aKey: "faq.certification.a3" },
        { qKey: "faq.certification.q4", aKey: "faq.certification.a4" },
      ]
    },
    {
      titleKey: "faq.category.toolkit",
      questions: [
        { qKey: "faq.toolkit.q1", aKey: "faq.toolkit.a1" },
        { qKey: "faq.toolkit.q2", aKey: "faq.toolkit.a2" },
        { qKey: "faq.toolkit.q3", aKey: "faq.toolkit.a3" },
        { qKey: "faq.toolkit.q4", aKey: "faq.toolkit.a4" },
        { qKey: "faq.toolkit.q5", aKey: "faq.toolkit.a5" },
      ]
    },
    {
      titleKey: "faq.category.products",
      questions: [
        { qKey: "faq.products.q1", aKey: "faq.products.a1" },
        { qKey: "faq.products.q2", aKey: "faq.products.a2" },
        { qKey: "faq.products.q3", aKey: "faq.products.a3" },
        { qKey: "faq.products.q4", aKey: "faq.products.a4" },
      ]
    },
    {
      titleKey: "faq.category.maintenance",
      questions: [
        { qKey: "faq.maintenance.q1", aKey: "faq.maintenance.a1" },
        { qKey: "faq.maintenance.q2", aKey: "faq.maintenance.a2" },
        { qKey: "faq.maintenance.q3", aKey: "faq.maintenance.a3" },
        { qKey: "faq.maintenance.q4", aKey: "faq.maintenance.a4" },
      ]
    }
  ];

  // Build FAQ structured data from all questions
  const allFaqItems = faqCategories.flatMap(cat =>
    cat.questions.map(item => ({
      "@type": "Question" as const,
      name: t(item.qKey),
      acceptedAnswer: {
        "@type": "Answer" as const,
        text: t(item.aKey),
      },
    }))
  );

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: allFaqItems,
  };

  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-4xl font-bold mb-4 text-foreground">{t("faq.title")}</h1>
        <div className="h-1 w-24 bg-primary mb-8"></div>
        
        <p className="text-lg text-muted-foreground mb-12">
          {t("faq.intro")}
        </p>

        <div className="space-y-12">
          {faqCategories.map((category, categoryIndex) => (
            <section key={categoryIndex}>
              <h2 className="text-2xl font-semibold mb-6 text-foreground">
                {t(category.titleKey)}
              </h2>
              
              <Accordion type="single" collapsible className="space-y-4">
                {category.questions.map((item, questionIndex) => (
                  <AccordionItem 
                    key={questionIndex} 
                    value={`${categoryIndex}-${questionIndex}`}
                    className="border border-border rounded-lg px-6 bg-card"
                  >
                    <AccordionTrigger className="text-left hover:no-underline py-4">
                      <span className="font-medium text-foreground pr-4">{t(item.qKey)}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-4 leading-relaxed">
                      {t(item.aKey)}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          ))}
        </div>

        {/* Still have questions section */}
        <div className="mt-16 p-8 bg-muted/50 rounded-lg border border-border">
          <h2 className="text-2xl font-semibold mb-4 text-foreground">{t("faq.stillQuestions.title")}</h2>
          <p className="text-muted-foreground mb-6">
            {t("faq.stillQuestions.description")}
          </p>
          <a 
            href="/contact" 
            className="inline-block px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
          >
            {t("faq.stillQuestions.button")}
          </a>
        </div>
      </div>

      {/* FAQPage JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
    </div>
  );
}
